import { Audio24Khz48KBitRateMonoMp3, SpeechSDK } from '@/hooks/useAzureSynthesizer';
import { logger } from '@/services/logger';
import { TtsData } from '@/services/tts/tts-data/types';
import { AudioError, convertErrorToAudioError, DEFAULT_VOICE, DEFAULT_VOICE_CONFIG } from '@/state/presenters';
import { WordLevelTimestamp } from '@/utils/types';
import { isWordLevelTimestampValid } from '@/utils/utils';
import { getSsml } from '@d-id/ssml';
import { IVoice, Providers, TtsCredentials, VoiceConfig } from '@d-id/types';
import * as franc from 'franc';
import * as langs from 'langs';
import escapeRegExp from 'lodash/escapeRegExp';
import { AudioConfig, SpeakerAudioDestination, SpeechConfig, SpeechSynthesizer } from 'microsoft-cognitiveservices-speech-sdk';

const getActualLanguage = (text: string) => {
    const langCode = franc.franc(text);
    return langs.where('3', langCode)?.name;
};

export const getVoiceId = (voice: IVoice | null, text: string, languageToVoices: Record<string, IVoice[]>, language?: string) => {
    const providedLanguage = language ?? voice?.language;

    const detectedLanguage = providedLanguage === 'multilingual' ? getActualLanguage(text) : providedLanguage;

    if (!detectedLanguage) return;
    const regex = new RegExp(escapeRegExp(detectedLanguage), 'i');

    const voiceOptions: IVoice[][] = [];
    for (const key in languageToVoices) {
        if (regex.test(key)) {
            voiceOptions.push(languageToVoices[key]);
        }
    }

    const compatibleMicrosoftVoices = voiceOptions.flat().filter(v => v.provider === Providers.Microsoft);
    const sameLanguageMatch = compatibleMicrosoftVoices.find(v => v.language === detectedLanguage);

    return sameLanguageMatch?.id ?? compatibleMicrosoftVoices?.[0]?.id;
};

/**
 * Get the tts data using the Azure SpeechSynthesizer
 * @param setInlineTtsError
 * @param token
 * @param text
 * @param voice
 * @param voiceConfig
 * @returns tts data
 */
interface Params {
    setInlineTtsError: (ttsError: AudioError) => void;
    credentials?: TtsCredentials;
    text: string;
    voice: IVoice | null;
    voiceConfig?: VoiceConfig;
    languageToVoices: Record<string, IVoice[]>;
}

export const getAzureTtsData = ({
    setInlineTtsError,
    credentials,
    text,
    voice,
    voiceConfig = DEFAULT_VOICE_CONFIG,
    languageToVoices,
}: Params): Promise<TtsData> => {
    return new Promise((resolve, reject) => {
        const wordLevelTimestamps: WordLevelTimestamp[] = [];

        if (!voice) {
            logger.error('No voice');
            return reject('No voice');
        }

        if (!credentials || !SpeechSDK) {
            logger.error('No credentials or SpeechSDK');
            return reject('No credentials or SpeechSDK');
        }

        const voiceId = ![Providers.Microsoft, Providers.AzureOpenAi].includes(voice.provider)
            ? getVoiceId(voice, text, languageToVoices) || DEFAULT_VOICE.id
            : voice.id;
        const { token, region } =
            voice.provider === Providers.AzureOpenAi ? credentials[Providers.AzureOpenAi] : credentials[Providers.Microsoft];
        const speechConfig = SpeechConfig.fromAuthorizationToken(token, region);
        speechConfig.speechSynthesisOutputFormat = Audio24Khz48KBitRateMonoMp3;
        const player = new SpeakerAudioDestination();
        const synthesizer = new SpeechSynthesizer(speechConfig, AudioConfig.fromSpeakerOutput(player));

        const handleClose = () => {
            synthesizer.close();
            player.close();
        };

        player.internalAudio.volume = 0;

        synthesizer.SynthesisCanceled = (_, e) => {
            handleClose();
            setInlineTtsError(convertErrorToAudioError(e));
            reject(e);
        };

        synthesizer.wordBoundary = async (_, event: any) => {
            const wordTimestamp = {
                word: event.text,
                startTime: event.audioOffset,
                endTime: event.audioOffset + event.duration,
            };
            wordLevelTimestamps.push(wordTimestamp);
        };

        synthesizer.speakSsmlAsync(
            getSsml(text, voiceId, { ...voiceConfig, volume: 100 }),
            params => {
                handleClose();

                if (params.audioData.byteLength === 0) {
                    setInlineTtsError('language');
                    reject('language');
                    return;
                }

                const duration = Math.ceil(params.audioDuration / 10000 / 1000);

                if (isWordLevelTimestampValid(wordLevelTimestamps)) {
                    resolve({ data: params.audioData, duration, wordLevelTiming: wordLevelTimestamps });
                } else {
                    resolve({ data: params.audioData, duration });
                }
            },
            (error: string) => {
                handleClose();
                reject(error);
            }
        );
    });
};
